import { useMutation } from "@tanstack/react-query";
import { db } from "../../../firebase/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import { APPLICATION_STATUS } from "enum/job-status-type";
import { CandidateInterface } from "./dto";
import { JobInterface } from "api/jobs/dto";

const flattenObject = (obj: any, prefix = ""): Record<string, string> => {
  if (obj === null || obj === undefined) {
    return {};
  }
  return Object.keys(obj).reduce((acc, key) => {
    const prefixedKey = prefix ? `${prefix}.${key}` : key;
    if (
      typeof obj[key] === "object" &&
      obj[key] !== null &&
      !(obj[key].toDate && typeof obj[key].toDate === "function")
    ) {
      Object.assign(acc, flattenObject(obj[key], prefixedKey));
    } else {
      if (obj[key]?.toDate && typeof obj[key].toDate === "function") {
        acc[prefixedKey] = obj[key].toDate().toISOString();
      } else {
        acc[prefixedKey] = String(obj[key] ?? "");
      }
    }
    return acc;
  }, {} as Record<string, string>);
};

const getAllCandidates = async (
  jobId: string | undefined
): Promise<CandidateInterface[]> => {
  if (!jobId) throw new Error("Job ID is required");

  const jobDocRef = doc(db, "jobs", jobId);
  const jobDocSnap = await getDoc(jobDocRef);
  if (!jobDocSnap.exists()) throw new Error("Job not found");
  const jobData = { ...jobDocSnap.data(), id: jobDocSnap.id } as JobInterface;

  const applicationsQuery = query(
    collection(db, "applications"),
    where("jobId", "==", jobId),
    where("status", "in", [
      APPLICATION_STATUS.SUBMITTED,
      APPLICATION_STATUS.SHORT_LISTED,
      APPLICATION_STATUS.REJECTED,
      APPLICATION_STATUS.APPROVED,
    ]),
    orderBy("appliedAt", "desc")
  );

  const applicationsSnap = await getDocs(applicationsQuery);
  const docs = applicationsSnap.docs;

  return docs.map((docSnap) => {
    const applicationData = docSnap.data();
    return {
      ...applicationData,
      id: docSnap.id,
      job: jobData,
    } as CandidateInterface;
  });
};

const generateCSVContent = (candidates: CandidateInterface[]): string => {
  const allKeys = new Set<string>();
  const flattenedCandidates = candidates.map((candidate) => {
    const flat = flattenObject(candidate);
    Object.keys(flat).forEach((key) => allKeys.add(key));
    return flat;
  });

  // DEBUGGING STEP 1: Log the entire flattened data structure.
  console.log("Data being converted to CSV:", flattenedCandidates);

  const headers = Array.from(allKeys).sort((a, b) => {
    if (a.startsWith("applicant_info") && !b.startsWith("applicant_info"))
      return -1;
    if (!a.startsWith("applicant_info") && b.startsWith("applicant_info"))
      return 1;
    return a.localeCompare(b);
  });

  // "Bulletproof" version of escapeCSV that prevents crashes.
  const escapeCSV = (value: any) => {
    const strValue = String(value ?? ""); // Force conversion to string
    if (strValue.includes(",") || strValue.includes('"') || strValue.includes("\n")) {
      return `"${strValue.replace(/"/g, '""')}"`;
    }
    return strValue;
  };

  const headerRow = headers.join(",");
  const dataRows = flattenedCandidates.map((candidate) =>
    headers.map((header) => escapeCSV(candidate[header])).join(",")
  );

  return [headerRow, ...dataRows].join("\n");
};

const downloadCSV = (content: string, jobTitle: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `${jobTitle.replace(/ /g, "_")}_candidates.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const useExtractAllDataCSV = () => {
  return useMutation({
    mutationFn: async (jobId: string | undefined) => {
      const candidates = await getAllCandidates(jobId);
      if (candidates.length === 0) {
        return { success: false, message: "No candidates to export." };
      }

      const csvContent = generateCSVContent(candidates);
      const jobTitle = candidates[0].job.jobTitle;
      downloadCSV(csvContent, jobTitle);
      return { success: true };
    },
    onSuccess: (result) => {
      if (!result.success) {
        // message.info(result.message);
      }
    },
    onError: (error) => {
      // message.error(error instanceof Error ? error.message : "Failed to extract CSV.");
      // DEBUGGING STEP 2: Log the full error to see the stack trace.
      console.error("CSV Extraction failed:", error);
    },
  });
};